import { Button } from "../../../button";
import { DataTable, type DataTableColumn } from "../../data-table";
import type {
  EnvironmentD1AdmissionResponse,
  CurrentEnvironmentProjectionMember,
  EnvironmentListResponse,
} from "../../../api/contracts/environments";

export type EnvironmentConsoleRow = {
  id: string;
  generationId: string;
  displayName: string;
  kind: string;
  topologyProfile: string;
  state: string;
  freshness: string;
  observedAt: string;
  cursor: string;
  reasonCode: string | null;
};

export type ProviderConnectionLifecycleProofRow = {
  id: string;
  label: string;
  state: string;
  semantics: string;
  operation: string;
  evidence: string;
  actions: Array<{
    id: string;
    label: string;
    disabledReason?: string;
  }>;
};

export type ProviderConnectionLifecycleAction = {
  rowId: string;
  actionId: string;
  operation: string;
  operationId?: string;
  authorizationId?: string;
  canonicalSignedAuthorizationBytes?: string;
  canonicalSignedTransitionBytes?: string;
  canonicalSignedTerminalEffectBytes?: string;
  canonicalSignedNextUnresolvedOutcomeBytes?: string;
  providerCapabilityClaimDigest?: string;
};

export type ProviderConnectionLifecycleActionState = {
  disabled?: boolean;
  reason?: string;
};

export type OperationAuthorityCaptureField =
  | "operationId"
  | "authorizationId"
  | "canonicalSignedAuthorizationBytes"
  | "canonicalSignedTransitionBytes"
  | "canonicalSignedTerminalEffectBytes"
  | "canonicalSignedNextUnresolvedOutcomeBytes"
  | "providerCapabilityClaimDigest";

export type OperationAuthorityCaptureState = Partial<
  Record<OperationAuthorityCaptureField, string>
>;

export const ENVIRONMENT_PROVIDER_CONNECTION_LIFECYCLE_PROOF: ProviderConnectionLifecycleProofRow[] =
  [
    {
      id: "authorize",
      label: "Authorize",
      state: "authorization-plan-ready",
      semantics: "Plan is prepared for human approval; execution waits for an approved plan.",
      operation: "provider-connection.authorize.plan",
      evidence: "No live provider operation is started by rendering the console.",
      actions: [
        {
          id: "environment.provider-connection.authorize.plan",
          label: "Plan",
          disabledReason: "server-render proof only",
        },
        {
          id: "environment.provider-connection.authorize.execute",
          label: "Execute approved",
          disabledReason: "requires approved plan",
        },
      ],
    },
    {
      id: "inventory",
      label: "Inventory",
      state: "inventory-current",
      semantics: "List, detail, and refresh inventory are exposed as lifecycle reads/refreshes.",
      operation: "provider-connections.list",
      evidence: "Inventory proof is route-level and does not enumerate a live account.",
      actions: [
        {
          id: "environment.provider-connections.list",
          label: "List",
          disabledReason: "server-render proof only",
        },
        {
          id: "environment.provider-connection.detail",
          label: "Detail",
          disabledReason: "requires selected connection",
        },
        {
          id: "environment.provider-connection.inventory-refresh",
          label: "Refresh inventory",
          disabledReason: "requires selected connection",
        },
      ],
    },
    {
      id: "reauthorize",
      label: "Reauthorize",
      state: "reauthorization-required",
      semantics: "Credential rotation creates a new human-approved reauthorization plan first.",
      operation: "provider-connection.reauthorize.plan",
      evidence: "Existing provider credentials are not touched by this proof state.",
      actions: [
        {
          id: "environment.provider-connection.reauthorize.plan",
          label: "Plan reauthorization",
          disabledReason: "requires selected connection",
        },
      ],
    },
    {
      id: "revoke-mid-operation",
      label: "Revoke mid-operation",
      state: "revoked-during-operation",
      semantics:
        "Revocation and operation cancel preserve effects and hold unknown work for review.",
      operation: "provider-connection.revoke.plan",
      evidence: "Mid-operation proof exposes stop_requested semantics without provider writes.",
      actions: [
        {
          id: "environment.provider-connection.revoke.plan",
          label: "Plan revocation",
          disabledReason: "requires selected connection",
        },
        {
          id: "environment.operation.cancel",
          label: "Cancel operation",
          disabledReason: "requires operation handle",
        },
      ],
    },
    {
      id: "unknown-reconciliation",
      label: "Unknown reconciliation",
      state: "reconciliation-required",
      semantics: "Unknown target outcomes require reconciliation before resume.",
      operation: "operation.reconcile",
      evidence: "Unresolved receipt digest remains visible until a reconciliation decision lands.",
      actions: [
        {
          id: "environment.operation.reconcile",
          label: "Reconcile",
          disabledReason: "requires unresolved receipt",
        },
      ],
    },
    {
      id: "expired-claim-refusal",
      label: "Expired-claim refusal",
      state: "expired-claim-refused",
      semantics: "Expired capability claims are refused and require a fresh authorization claim.",
      operation: "provider-connection.claim.refuse-expired",
      evidence: "Refusal is rendered as terminal; no refresh or resume action is offered.",
      actions: [
        {
          id: "environment.provider-connection.expired-claim.refuse",
          label: "Refused",
          disabledReason: "claim expired",
        },
      ],
    },
  ];

function memberRow(member: CurrentEnvironmentProjectionMember): EnvironmentConsoleRow {
  if (member.state === "error") {
    return {
      id: member.environmentId,
      generationId: member.generationId,
      displayName: member.environmentId,
      kind: "unavailable",
      topologyProfile: "unknown",
      state: member.state,
      freshness: "unavailable",
      observedAt: "unavailable",
      cursor: "unavailable",
      reasonCode: member.reasonCode,
    };
  }

  return {
    id: member.environmentId,
    generationId: member.generationId,
    displayName: member.data.environment.displayName,
    kind: member.data.environment.kind,
    topologyProfile: member.data.environment.topologyProfile,
    state: member.state,
    freshness: member.refreshContact,
    observedAt: member.observedAt,
    cursor: member.cursor,
    reasonCode: member.reasonCode,
  };
}

function projectionRows(list: EnvironmentListResponse | null | undefined): EnvironmentConsoleRow[] {
  if (!list || list.payload.projectionKind !== "current") return [];
  return list.payload.members.map(memberRow);
}

function projectionCompleteness(list: EnvironmentListResponse): string {
  return list.payload.projectionKind === "current"
    ? list.payload.completeness
    : list.payload.emptyReason;
}

function supportedProjection(list: EnvironmentListResponse | null | undefined) {
  if (
    !list ||
    (list.payload.projectionKind !== "current" && list.payload.projectionKind !== "current-empty")
  ) {
    return null;
  }
  return list;
}

const columns: DataTableColumn<EnvironmentConsoleRow>[] = [
  {
    key: "environment",
    header: "Environment",
    render: (row) => (
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate type-body font-medium text-foreground">{row.displayName}</span>
        <code className="type-subtext text-muted-foreground">{row.id}</code>
      </span>
    ),
  },
  {
    key: "generation",
    header: "Generation",
    render: (row) => <code className="type-subtext text-muted-foreground">{row.generationId}</code>,
  },
  { key: "kind", header: "Kind", render: (row) => row.kind },
  { key: "topology", header: "Topology", render: (row) => row.topologyProfile },
  { key: "state", header: "Projection", render: (row) => row.state },
  { key: "contact", header: "Contact", render: (row) => row.freshness },
  { key: "observed", header: "Observed", render: (row) => row.observedAt },
  { key: "cursor", header: "Cursor", render: (row) => row.cursor },
  { key: "reason", header: "Reason", render: (row) => row.reasonCode ?? row.freshness },
];

const providerConnectionLifecycleColumns: DataTableColumn<ProviderConnectionLifecycleProofRow>[] = [
  {
    key: "state",
    header: "Lifecycle state",
    render: (row) => (
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate type-body font-medium text-foreground">{row.label}</span>
        <code className="type-subtext text-muted-foreground">{row.state}</code>
      </span>
    ),
  },
  {
    key: "operation",
    header: "Operation",
    render: (row) => <code className="type-subtext text-muted-foreground">{row.operation}</code>,
  },
  { key: "semantics", header: "Semantics", render: (row) => row.semantics },
  { key: "evidence", header: "Proof", render: (row) => row.evidence },
];

export function EnvironmentConsole({
  list,
  d1Admission,
  d1AdmissionError,
  onOpenEnvironment,
  providerConnectionLifecycle = ENVIRONMENT_PROVIDER_CONNECTION_LIFECYCLE_PROOF,
  onProviderConnectionAction,
  providerConnectionActionState,
  operationAuthorityCapture,
  onOperationAuthorityCaptureChange,
}: {
  list: EnvironmentListResponse | null | undefined;
  d1Admission?: EnvironmentD1AdmissionResponse | null;
  d1AdmissionError?: string | null;
  onOpenEnvironment?: (environmentId: string) => void;
  providerConnectionLifecycle?: ProviderConnectionLifecycleProofRow[] | null;
  onProviderConnectionAction?: (action: ProviderConnectionLifecycleAction) => void;
  providerConnectionActionState?: (
    action: ProviderConnectionLifecycleAction,
  ) => ProviderConnectionLifecycleActionState;
  operationAuthorityCapture?: OperationAuthorityCaptureState;
  onOperationAuthorityCaptureChange?: (
    field: OperationAuthorityCaptureField,
    value: string,
  ) => void;
}) {
  const summaryList = supportedProjection(list);
  const projectionSummary = summaryList
    ? {
        projection: summaryList.payload,
        completeness: projectionCompleteness(summaryList),
      }
    : null;
  const rows = projectionRows(list);
  const providerConnectionColumns: DataTableColumn<ProviderConnectionLifecycleProofRow>[] = [
    ...providerConnectionLifecycleColumns,
    {
      key: "actions",
      header: "Actions",
      render: (row) => (
        <span className="flex flex-wrap gap-1.5">
          {row.actions.map((action) => {
            const actionContext = {
              rowId: row.id,
              actionId: action.id,
              operation: row.operation,
              operationId: operationAuthorityCapture?.operationId,
              authorizationId: operationAuthorityCapture?.authorizationId,
              canonicalSignedAuthorizationBytes:
                operationAuthorityCapture?.canonicalSignedAuthorizationBytes,
              canonicalSignedTransitionBytes:
                operationAuthorityCapture?.canonicalSignedTransitionBytes,
              canonicalSignedTerminalEffectBytes:
                operationAuthorityCapture?.canonicalSignedTerminalEffectBytes,
              canonicalSignedNextUnresolvedOutcomeBytes:
                operationAuthorityCapture?.canonicalSignedNextUnresolvedOutcomeBytes,
              providerCapabilityClaimDigest:
                operationAuthorityCapture?.providerCapabilityClaimDigest,
            };
            const actionState = providerConnectionActionState?.(actionContext);
            const disabledReason = actionState?.disabled
              ? (actionState.reason ?? action.disabledReason)
              : onProviderConnectionAction
                ? undefined
                : (action.disabledReason ?? "provider lifecycle action unavailable");
            const disabled = Boolean(disabledReason);
            return (
              <Button
                key={action.id}
                type="button"
                variant="outline"
                size="sm"
                data-action-id={action.id}
                data-action-state={disabled ? "disabled" : "enabled"}
                data-operation={row.operation}
                data-lifecycle-state={row.state}
                data-operation-id={operationAuthorityCapture?.operationId || undefined}
                data-authorization-id={operationAuthorityCapture?.authorizationId || undefined}
                disabled={disabled}
                title={disabledReason}
                onClick={disabled ? undefined : () => onProviderConnectionAction?.(actionContext)}
              >
                {action.label}
              </Button>
            );
          })}
        </span>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-3" data-environment-console="">
      <D1AdmissionPanel admission={d1Admission} error={d1AdmissionError} />
      {projectionSummary ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-environment-projection-summary="">
          <SummaryStat label="Projection" value={projectionSummary.projection.projectionKind} />
          <SummaryStat label="Completeness" value={projectionSummary.completeness} />
          <SummaryStat
            label="Members"
            value={String(projectionSummary.projection.memberSlotCount)}
          />
        </div>
      ) : null}
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => `${row.id}:${row.generationId}`}
        wrapInCard={false}
        empty="No authorized Environment projections are available."
        onRowClick={onOpenEnvironment ? (row) => onOpenEnvironment(row.id) : undefined}
        rowActionId={onOpenEnvironment ? "environments.open" : undefined}
      />
      {providerConnectionLifecycle ? (
        <div className="flex flex-col gap-2" data-provider-connection-lifecycle-console="">
          <div>
            <h2 className="type-body font-medium text-foreground">Provider Connections</h2>
            <p className="mt-1 type-subtext text-muted-foreground">
              Human lifecycle proof states for provider authorization, inventory, rotation,
              revocation, reconciliation, and claim refusal.
            </p>
          </div>
          <OperationAuthorityCaptureControls
            value={operationAuthorityCapture}
            onChange={onOperationAuthorityCaptureChange}
          />
          <DataTable
            rows={providerConnectionLifecycle}
            columns={providerConnectionColumns}
            rowKey={(row) => row.id}
            wrapInCard={false}
            empty="No provider-connection lifecycle proof states are available."
          />
        </div>
      ) : null}
    </section>
  );
}

function D1AdmissionPanel({
  admission,
  error,
}: {
  admission?: EnvironmentD1AdmissionResponse | null;
  error?: string | null;
}) {
  const checks = admission?.checks ?? [];
  const statusClass = (status: string) =>
    status === "pass"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : status === "observed"
        ? "border-sky-200 bg-sky-50 text-sky-900"
        : status === "blocked"
          ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-border bg-muted text-muted-foreground";
  return (
    <div className="rounded-md border border-border bg-background p-3" data-d1-admission="">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="type-body font-medium text-foreground">D1 Admission</h2>
          <p className="mt-1 type-subtext text-muted-foreground">
            Current account, repository, Environment, MCP setup, task, and agent-observation facts.
          </p>
        </div>
        <code className="type-subtext text-muted-foreground">
          {admission ? admission.kickoffSha.slice(0, 12) : "not loaded"}
        </code>
      </div>
      {error ? (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 type-subtext text-amber-900">
          {error}
        </div>
      ) : null}
      {admission ? (
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-4">
          <SummaryStat label="Account" value={admission.account.state} />
          <SummaryStat label="Repository" value={admission.repository.state} />
          <SummaryStat label="Environment" value={admission.environment.state} />
          <SummaryStat label="MCP setup" value={admission.mcpSetup.state} />
        </div>
      ) : null}
      <div className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-2">
        {checks.map((check) => (
          <div
            key={check.id}
            className={`rounded border p-2 type-subtext ${statusClass(check.status)}`}
            data-d1-admission-check={check.id}
            data-d1-admission-status={check.status}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{check.label}</span>
              <span className="uppercase">{check.status}</span>
            </div>
            <p className="mt-1">{check.evidence}</p>
            <p className="mt-1 opacity-80">{check.nextAction}</p>
          </div>
        ))}
        {!admission && !error ? (
          <div className="rounded border border-border bg-muted p-2 type-subtext text-muted-foreground">
            D1 admission state is loading.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function OperationAuthorityCaptureControls({
  value,
  onChange,
}: {
  value?: OperationAuthorityCaptureState;
  onChange?: (field: OperationAuthorityCaptureField, value: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2" data-operation-authority-capture="">
      <CaptureInput
        label="Operation handle"
        field="operationId"
        value={value?.operationId}
        onChange={onChange}
      />
      <CaptureInput
        label="Authorization id"
        field="authorizationId"
        value={value?.authorizationId}
        onChange={onChange}
      />
      <CaptureInput
        label="Provider claim digest"
        field="providerCapabilityClaimDigest"
        value={value?.providerCapabilityClaimDigest}
        onChange={onChange}
      />
      <CaptureTextArea
        label="Signed authorization bytes"
        field="canonicalSignedAuthorizationBytes"
        value={value?.canonicalSignedAuthorizationBytes}
        onChange={onChange}
      />
      <CaptureTextArea
        label="Signed transition bytes"
        field="canonicalSignedTransitionBytes"
        value={value?.canonicalSignedTransitionBytes}
        onChange={onChange}
      />
      <CaptureTextArea
        label="Signed terminal effect bytes"
        field="canonicalSignedTerminalEffectBytes"
        value={value?.canonicalSignedTerminalEffectBytes}
        onChange={onChange}
      />
      <CaptureTextArea
        label="Signed next unresolved outcome bytes"
        field="canonicalSignedNextUnresolvedOutcomeBytes"
        value={value?.canonicalSignedNextUnresolvedOutcomeBytes}
        onChange={onChange}
      />
    </div>
  );
}

function CaptureInput({
  label,
  field,
  value,
  onChange,
}: {
  label: string;
  field: OperationAuthorityCaptureField;
  value?: string;
  onChange?: (field: OperationAuthorityCaptureField, value: string) => void;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1 type-subtext font-medium text-muted-foreground">
      <span>{label}</span>
      <input
        className="min-h-9 rounded border border-border bg-background px-2 py-1 font-mono type-subtext text-foreground"
        data-operation-authority-field={field}
        value={value ?? ""}
        readOnly={!onChange}
        onChange={(event) => onChange?.(field, event.currentTarget.value)}
      />
    </label>
  );
}

function CaptureTextArea({
  label,
  field,
  value,
  onChange,
}: {
  label: string;
  field: OperationAuthorityCaptureField;
  value?: string;
  onChange?: (field: OperationAuthorityCaptureField, value: string) => void;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1 type-subtext font-medium text-muted-foreground">
      <span>{label}</span>
      <textarea
        className="min-h-20 resize-y rounded border border-border bg-background px-2 py-1 font-mono type-subtext text-foreground"
        data-operation-authority-field={field}
        value={value ?? ""}
        readOnly={!onChange}
        onChange={(event) => onChange?.(field, event.currentTarget.value)}
      />
    </label>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="type-subtext font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 truncate type-body font-medium text-foreground">{value}</div>
    </div>
  );
}
